import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Input, Button, Space, Typography, Spin, Empty } from 'antd';
import {
  CalendarOutlined,
  UserOutlined,
  ArrowRightOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './Home.css';
import './Blog.css';

const { Paragraph } = Typography;

const PLACEHOLDER = 'https://placehold.co/600x400/e2e8f0/64748b?text=Blog';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const apiBase = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    const load = async () => {
      try {
        const [postsRes, catsRes] = await Promise.all([
          fetch(`${apiBase}/blog`).then((r) => (r.ok ? r.json() : { data: [] })).catch(() => ({ data: [] })),
          fetch(`${apiBase}/blog/categories`).then((r) => (r.ok ? r.json() : { data: [] })).catch(() => ({ data: [] })),
        ]);
        const allPosts = postsRes.data || [];
        setPosts(allPosts.filter((p) => !p.is_featured));
        setFeatured(allPosts.find((p) => p.is_featured) || allPosts[0] || null);
        setCategories(catsRes.data || []);
      } catch {
        console.error('Failed to load blog');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const displayed = search
    ? posts.filter(
        (p) =>
          p.title.toLowerCase().includes(search.toLowerCase()) ||
          (p.author || '').toLowerCase().includes(search.toLowerCase()) ||
          (p.category || '').toLowerCase().includes(search.toLowerCase()),
      )
    : posts;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '96px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Featured post */}
      {featured && (
        <section className="home-section" style={{ paddingBottom: 40 }}>
          <div className="blog-featured">
            <Link
              to={`/blog/${featured.slug || featured.id}`}
              className="blog-featured-media"
              aria-label={featured.title}
            >
              <img
                src={featured.image_url || PLACEHOLDER}
                alt={featured.title}
                loading="lazy"
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER; }}
              />
            </Link>
            <div className="blog-featured-body">
              <span className="blog-chip" style={{ alignSelf: 'flex-start' }}>
                Featured
              </span>
              <h2>
                <Link to={`/blog/${featured.slug || featured.id}`} className="blog-featured-link">
                  {featured.title}
                </Link>
              </h2>
              <Paragraph style={{ color: '#64748b', fontSize: '1rem', marginBottom: 18 }}>
                {featured.excerpt}
              </Paragraph>
              <div className="blog-meta" style={{ borderTop: 'none', paddingTop: 0, marginTop: 0 }}>
                <span>
                  <UserOutlined />
                  {featured.author}
                </span>
                <span>
                  <CalendarOutlined />
                  {new Date(featured.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                <span>{featured.read_time}</span>
              </div>
              <div style={{ marginTop: 22 }}>
                <Link to={`/blog/${featured.slug || featured.id}`}>
                  <Button
                    type="primary"
                    size="large"
                    icon={<ArrowRightOutlined />}
                    iconPosition="end"
                  >
                    Read full article
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="home-section" style={{ paddingTop: 0 }}>
        <Row gutter={[32, 32]}>
          {/* Articles grid */}
          <Col xs={24} lg={16}>
            <h2 className="mb-6 font-display text-2xl font-extrabold text-slate-900">
              Latest articles
            </h2>
            {displayed.length === 0 ? (
              <Empty description="No articles found" />
            ) : (
              <Row gutter={[24, 24]}>
                {displayed.map((post) => (
                  <Col xs={24} sm={12} key={post.id}>
                    <Link to={`/blog/${post.slug || post.id}`} className="blog-card-link">
                      <article className="blog-card">
                        <div className="blog-cover">
                          <img
                            src={post.image_url || PLACEHOLDER}
                            alt={post.title}
                            loading="lazy"
                            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PLACEHOLDER; }}
                          />
                          <span className="blog-chip on-cover">{post.category}</span>
                        </div>
                        <div className="blog-card-body">
                          <div
                            className="blog-meta"
                            style={{ borderTop: 'none', paddingTop: 0, marginTop: 0 }}
                          >
                            <span>
                              <UserOutlined />
                              {post.author}
                            </span>
                            <span>
                              <CalendarOutlined />
                              {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                          <h3 className="blog-card-title">{post.title}</h3>
                          <p className="blog-excerpt">{post.excerpt}</p>
                          <div className="blog-meta">
                            <span>{post.read_time}</span>
                            <span className="blog-readmore" style={{ marginLeft: 'auto' }}>
                              Read more <ArrowRightOutlined style={{ fontSize: 12 }} />
                            </span>
                          </div>
                        </div>
                      </article>
                    </Link>
                  </Col>
                ))}
              </Row>
            )}
          </Col>

          {/* Sidebar */}
          <Col xs={24} lg={8}>
            <div className="blog-side-card" style={{ marginBottom: 24 }}>
              <div className="blog-side-title">Categories</div>
              {categories.length > 0
                ? categories.map((cat) => (
                    <div className="blog-cat-row" key={cat.category}>
                      <span>{cat.category}</span>
                      <span className="blog-cat-count">{cat.count}</span>
                    </div>
                  ))
                : (
                    <p style={{ color: '#94a3b8', padding: '8px 0' }}>No categories yet</p>
                  )}
            </div>

            <div className="blog-side-card blog-newsletter" style={{ marginBottom: 24 }}>
              <div className="blog-side-title">Subscribe to our newsletter</div>
              <p style={{ color: '#cbd5e1', marginBottom: 16 }}>
                Get buying guides, repair tips, and exclusive deals delivered to your inbox.
              </p>
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Input size="large" placeholder="you@example.com" prefix={<MailOutlined />} />
                <Button type="primary" size="large" block>
                  Subscribe now
                </Button>
              </Space>
            </div>

            <div className="blog-side-card">
              <div className="blog-side-title">Popular posts</div>
              {posts.slice(0, 3).map((post) => (
                <Link to={`/blog/${post.slug || post.id}`} className="blog-popular-row" key={post.id}>
                  <h4>{post.title}</h4>
                  <div
                    className="blog-meta"
                    style={{ borderTop: 'none', paddingTop: 0, marginTop: 0 }}
                  >
                    <span>
                      {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <span>{post.read_time}</span>
                  </div>
                </Link>
              ))}
            </div>
          </Col>
        </Row>
      </section>
    </div>
  );
};

export default Blog;
